import { Inject } from '@odinjs/odin';

import { APPLICATION_ROUTES, catchRenderException, Endpoint, FOUNDATION_ROUTES, pollingService, Route, shouldRenderMobile } from '@philips-emr/hpms-provider-accreditation-commons';
import TermsService from '@philips-emr/hpms-provider-accreditation-commons/lib/services/terms-service';
import { AvatarAPI, FetchAPI, NotificationCenterAPI, PanelAPI } from '@philips-emr/tws-components';
import { ThemeTonalRange, Type } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnClick, OnMount } from '@philips-emr/tws-core/lib/client';
import { ClickEvent, FetchEvent, MountEvent } from '@philips-emr/tws-core/lib/component/types';
import { AuthenticationService, HttpService, ThemeService } from '@philips-emr/tws-core/lib/services';
import { ApplicationBarAPI } from '@philips-emr/tws-foundation';
import { applicationContext, foundationContext, OpenModuleOptions } from '@philips-emr/tws-foundation/lib/context';

import { HomeAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

enum NOTIFICATION_CLICK_TYPE {
  NOTIFICATION = 'NotificationClick',
  MARK_AS_READ = 'MarkAsRead',
  MARK_ALL_AS_READ = 'MarkAllAsRead',
}

enum NotificationStatus {
  NO = 'N',
  YES = 'S',
}
interface Notification {
  dtCreation: string,
  nrSequence: string,
  dsTitle: string,
  dsText: string,
  dtNotification: string,
  ieRead: NotificationStatus,
  qtdUnread: number;
}
const pollingTimeNotification = 360000;
@Controller({ fragment: FragmentNames.HOME })
export default class HomeController extends BaseController {

  @Inject(Aliases.APPLICATION_BAR)
  private applicationBar: ApplicationBarAPI;
  @Inject(Aliases.FETCH_NOTIFICATION)
  private fetchNotification: FetchAPI;
  @Inject(Aliases.NOTIFICATION_CENTER)
  private notificationCenter: NotificationCenterAPI;

  @OnClick(Aliases.APPLICATION_BAR)
  handleApplicationBarAvatarClick(): void {
    this.historyService.push(Route.MY_DATA);
    this.applicationBar.setState({ openSpotlight: false });
  }

  @OnMount(Aliases.PANEL_ROUTES)
  onMountRoutes(): void {
    const { nrSeqUser } = this.historyService.getCurrentRouteParams();
    const openModuleOptions: OpenModuleOptions = {
      at: {
        params: { nrSeqUser },
        path: APPLICATION_ROUTES.PROVIDER_ACCREDITATION,
        skip: true,
      },
    };
    applicationContext.openModule('accreditation', openModuleOptions);
    applicationContext.openModule('technical-visit', { preventNavigation: true });
    applicationContext.openModule('negotiation', { preventNavigation: true });
    applicationContext.openModule('calls', { preventNavigation: true });
  }

  @OnMount(Aliases.AVATAR)
  onMountAvatar(event: MountEvent): void {
    const api = event.target as AvatarAPI;
    const user = foundationContext.getUser();
    if (user && !shouldRenderMobile()) {
      let userName = user.name;
      if (userName && userName.length > 30) {
        userName = `${userName.substring(0, 30)}...`;
      }
      api.setTitle(userName);
    }
  }

  @OnMount(Aliases.LAYOUT_PROVIDER_ACCREDITATION)
  onMount(): void {
    ThemeService.setContentTonalRange(ThemeTonalRange.ULTRA_LIGHT);
    const { nrSeqUser } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqUser });
    TermsService.checkTerms();  }

  @OnMount(Aliases.NOTIFICATION_CENTER)
  onMountNotificacionCenter(): void {
    pollingService.createPolling('notification', this.notificationsUnreads.bind(this), pollingTimeNotification);
    this.notificationsUnreads();
  }

  @OnClick(Aliases.OPTION_EXIT)
  onExitClick(): void {
    pollingService.removePolling('notification');
    void AuthenticationService.logout();
    applicationContext.redirect(FOUNDATION_ROUTES.LOGIN);
  }

  @OnClick(Aliases.OPTION_MY_DATA)
  onClickMeusDados(): void {
    applicationContext.redirect(Route.MY_DATA);
  }

  @OnMount(Aliases.PANEL_APP_ROUTES)
  onMountMain(event: MountEvent): void {
    const api = event.target as PanelAPI;
    this.validatePanel(api, false);
    this.historyService.addListener((path: string) => {
      if (this.shouldRenderMargins(path)) {
        api.setVisible(false);
      } else {
        catchRenderException(() => {
          api.setVisible(true);
        });
      }
      return Promise.resolve();
    });
  }

  @OnMount(Aliases.PANEL_ACCREDITATION)
  onMountAccreditation(event: MountEvent): void {
    const api = event.target as PanelAPI;
    this.validatePanel(api, true);
    this.historyService.addListener((path: string) => {
      if (this.shouldRenderMargins(path)) {
        catchRenderException(() => {
          api.setVisible(true);
        });
      } else {
        api.setVisible(false);
      }
      return Promise.resolve();
    });
  }

  @OnClick(Aliases.NOTIFICATION_CENTER)
  onNotificationClick(event: ClickEvent): void {
    switch (event.value.type) {
      case NOTIFICATION_CLICK_TYPE.MARK_ALL_AS_READ:
        this.handleMarkAllAsReadClick();
        break;
      case NOTIFICATION_CLICK_TYPE.MARK_AS_READ:
        this.handleMarkAsReadClick(event);
        break;
    }
  }

  @OnAfterFetch(Aliases.FETCH_NOTIFICATION)
  onAfterFetchNotification(fetchEvent: FetchEvent): void {
    const notifications = fetchEvent.data as Notification[];
    const notificationFormattedData = notifications.map((data, index) => {
      const isNewPage = index >= (notifications.length - 15);

      if (!isNewPage) {
        return data;
      }

      const isRead = data.ieRead === 'S';

      return {
        date: data.dtNotification,
        description: data.dsText,
        id: data.nrSequence,
        read: isRead,
        title: data.dsTitle,
        type: Type.NOTICE,
      };
    });

    this.fetchNotification.setState({ data: notificationFormattedData });
  }

  private shouldRenderMargins(path: string): boolean {

    return (path.includes('tipo-credenciamento') && !path.includes('novo')) || path.includes('agendamentos')
      || path.includes('inconsistencias') || path.includes('formularios')
      || path.includes('proposta');
  }

  public notificationsUnreads(): void {
    const { nrSeqUser } = this.historyService.getCurrentRouteParams();
    if (Number(nrSeqUser)) {
      void HttpService.get(Endpoint.GET.NOTIFICATIONS_UNREAD, { nrSeqUser }).then(response => {
        const { body } = response;
        const { qtdUnread } = body as Notification;
        this.notificationCenter.setUnreadNotificationsCount(qtdUnread);
      });
    }
  }

  private handleMarkAllAsReadClick(): void {
    const { nrSeqUser } = this.historyService.getCurrentRouteParams();
    void HttpService.post(Endpoint.POST.NOTIFICATION_UPDATE_ALL_TO_READ, { nrSeqUser }, {}).then(() => {
      this.notificationCenter.markAllAsRead();
      this.notificationsUnreads();
    });
  }

  private handleMarkAsReadClick(event: ClickEvent): void {
    const { id } = event.value.item;
    this.updateNotificationStatus(id);
  }

  private updateNotificationStatus(nrSequence: number): void {
    const { nrSeqUser } = this.historyService.getCurrentRouteParams();
    void HttpService.post(Endpoint.POST.NOTIFICATION_UPDATE_TO_READ, { nrSeqUser, nrSequence }, {}).then(() => {
      this.notificationCenter.setReadStatus(nrSequence, true);
      const qtdUnread = this.notificationCenter.getUnreadNotificationCount();
      this.notificationCenter.setUnreadNotificationsCount(qtdUnread - 1);
    });
  }

  private validatePanel(api: PanelAPI, visible: boolean): void {
    if (this.shouldRenderMargins(this.historyService.getCurrentRoute())) {
      api.setVisible(visible);
    } else {
      catchRenderException(() => {
        api.setVisible(!visible);
      });
    }
  }

}
